import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  FileCopy,
  Call,
  CallEnd,
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  Send,
  Link as LinkIcon,
  ExpandMore,
  ExpandLess,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { SocketContext } from "../Context";

const SECRET = "ca93F3v3G2UsQG3Q1nGm";

const useStyles = makeStyles((theme) => ({
  sidebar: {
    width: "340px",
    background: "#202124",
    borderLeft: "1px solid #3c4043",
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 60px)",
    overflow: "auto",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      height: "auto",
      borderLeft: "none",
      borderTop: "1px solid #3c4043",
      maxHeight: "none",
      overflow: "visible",
    },
    "@media (orientation: landscape) and (max-width: 960px)": {
      maxHeight: "none",
    },
  },
  section: { padding: "14px 16px", borderBottom: "1px solid #3c4043" },
  sectionTitle: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#9aa0a6",
    marginBottom: "10px",
    textTransform: "uppercase",
  },
  mobileSettingsToggleWrap: {
    display: "none",
    [theme.breakpoints.down("md")]: { display: "block", padding: "10px 16px", borderBottom: "1px solid #3c4043" },
  },
  mobileSettingsToggle: {
    width: "100%",
    borderRadius: "10px",
    padding: "10px 12px",
    background: "#3c4043",
    color: "#e8eaed",
    textTransform: "none",
    fontSize: "13px",
    justifyContent: "space-between",
  },
  desktopOnly: {
    display: "block",
    [theme.breakpoints.down("md")]: { display: "none" },
  },
  mobileSettingsPanel: {
    display: "none",
    [theme.breakpoints.down("md")]: { display: "block" },
  },
  textField: {
    marginBottom: "8px",
    "& .MuiOutlinedInput-root": {
      background: "#3c4043",
      borderRadius: "8px",
      color: "#e8eaed",
      "& fieldset": { borderColor: "#5f6368" },
    },
    "& .MuiInputLabel-root": { color: "#9aa0a6" },
  },
  selectWrap: { marginBottom: "8px" },
  selectRoot: { background: "#3c4043", color: "#e8eaed", borderRadius: "8px" },
  savedName: { color: "#34a853", fontSize: "12px", marginBottom: "8px", fontWeight: 600 },
  meterWrap: { marginTop: "6px", marginBottom: "10px", background: "#3c4043", borderRadius: "8px", height: "8px", overflow: "hidden" },
  meterBar: { height: "100%", background: "#34a853", transition: "width 120ms linear" },
  meterLabel: { fontSize: "11px", color: "#9aa0a6" },
  button: {
    borderRadius: "24px",
    padding: "8px 18px",
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 600,
    width: "100%",
    marginBottom: "8px",
    minHeight: "40px",
  },
  copyButton: { background: "#3c4043", color: "#e8eaed" },
  callButton: { background: "#1a73e8", color: "#fff" },
  hangupButton: { background: "#ea4335", color: "#fff" },
  controls: { display: "flex", gap: "10px", justifyContent: "center", padding: "12px" },
  controlButton: { width: "46px", height: "46px", background: "#3c4043", color: "#e8eaed" },
  status: { color: "#9aa0a6", fontSize: "12px" },
  chatList: {
    maxHeight: "180px",
    overflowY: "auto",
    background: "#111",
    border: "1px solid #3c4043",
    borderRadius: "8px",
    padding: "8px",
    marginBottom: "8px",
  },
  chatListEmpty: {
    display: "none",
  },
  chatMessage: { marginBottom: "8px", color: "#e8eaed", fontSize: "12px", lineHeight: 1.35, wordBreak: "break-word" },
  chatMeta: { color: "#9aa0a6", fontSize: "11px" },
  chatInputRow: { display: "flex", gap: "8px", alignItems: "center" },
  chatInput: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      background: "#3c4043",
      borderRadius: "8px",
      color: "#e8eaed",
      "& fieldset": { borderColor: "#5f6368" },
    },
  },
  sendBtn: { minWidth: "42px", height: "40px", borderRadius: "8px", background: "#1a73e8", color: "#fff" },
  roomWrap: {
    marginTop: "6px",
    background: "#111",
    border: "1px solid #3c4043",
    borderRadius: "8px",
    padding: "10px",
    textAlign: "center",
    cursor: "pointer",
  },
  qrImage: { width: "150px", height: "150px", borderRadius: "6px", background: "#fff" },
  roomHint: {
    marginTop: "8px",
    color: "#9aa0a6",
    fontSize: "11px",
    lineHeight: 1.35,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "6px",
  },
  roomCopied: { marginTop: "8px", color: "#34a853", fontSize: "11px", fontWeight: 600 },
}));

const Sidebar = ({ children }) => {
  const {
    me,
    callAccepted,
    name,
    setName,
    callEnded,
    stream,
    leaveCall,
    callUser,
    toggleMute,
    toggleVideo,
    micLevel,
    roomMessages,
    sendRoomMessage,
  } = useContext(SocketContext);

  const p = new URLSearchParams(window.location.search);
  const token = p.get("token") || "";
  const secret = p.get("secret") || "";
  const isTokenMode = Boolean(token && secret === SECRET);

  const [idToCall, setIdToCall] = useState("");
  const [copied, setCopied] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [chatText, setChatText] = useState("");
  const [roomCopied, setRoomCopied] = useState(false);
  const [showSettingsMobile, setShowSettingsMobile] = useState(false);

  const chatBottomRef = useRef(null);
  const c = useStyles();

  const roomJoinUrl = window.location.href;
  const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(roomJoinUrl)}`;

  useEffect(() => {
    if (!name?.trim()) {
      setShowSaved(false);
      return;
    }
    setShowSaved(true);
    const t = setTimeout(() => setShowSaved(false), 5000);
    return () => clearTimeout(t);
  }, [name]);

  useEffect(() => {
    const audioTrack = stream?.getAudioTracks?.()[0];
    if (!audioTrack) return;
    setIsMuted(audioTrack.enabled === false);
  }, [stream]);

  const onSendChat = () => {
    const value = (chatText || "").trim();
    if (!value) return;
    sendRoomMessage(value);
    setChatText("");
  };

  const onChatEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSendChat();
    }
  };

  const onCopyRoomLink = async () => {
    try {
      await navigator.clipboard.writeText(roomJoinUrl);
      setRoomCopied(true);
      setTimeout(() => setRoomCopied(false), 2000);
    } catch (e) {
      console.error("copy room link failed", e);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    try {
      return new Date(Number(ts)).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const settingsBlock = (
    <>
      <div className={c.section}>
        <Typography className={c.sectionTitle}>Profil</Typography>
        <TextField
          label="Ditt namn"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          className={c.textField}
          variant="outlined"
          size="small"
        />
        {showSaved && <Typography className={c.savedName}>✅ Namn sparat</Typography>}



        <Typography className={c.meterLabel}>Mikrofonnivå</Typography>
        <div className={c.meterWrap}>
          <div className={c.meterBar} style={{ width: `${Math.max(2, micLevel)}%` }} />
        </div>

        {!isTokenMode && (
          <CopyToClipboard text={me} onCopy={() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
            <Button variant="contained" fullWidth startIcon={<FileCopy />} className={`${c.button} ${c.copyButton}`}>
              {copied ? "ID kopierat" : "Kopiera ditt ID"}
            </Button>
          </CopyToClipboard>
        )}      </div>

      {!isTokenMode && (
        <div className={c.section}>
          <Typography className={c.sectionTitle}>Join a Call</Typography>
          <TextField
            label="Enter ID to Call"
            value={idToCall}
            onChange={(e) => setIdToCall(e.target.value)}
            fullWidth
            className={c.textField}
            variant="outlined"
            size="small"
          />
          {callAccepted && !callEnded ? (
            <Button variant="contained" startIcon={<CallEnd />} fullWidth onClick={leaveCall} className={`${c.button} ${c.hangupButton}`}>Avsluta samtal</Button>
          ) : (
            <Button variant="contained" startIcon={<Call />} fullWidth onClick={() => callUser(idToCall)} className={`${c.button} ${c.callButton}`} disabled={!idToCall}>Call</Button>
          )}
        </div>
      )}

      <div className={c.controls}>
        <Tooltip title={isMuted ? "Unmute" : "Mute"}>
          <IconButton className={c.controlButton} onClick={() => { toggleMute(); setIsMuted(!isMuted); }}>{isMuted ? <MicOff /> : <Mic />}</IconButton>
        </Tooltip>
        <Tooltip title={isVideoOff ? "Turn on camera" : "Turn off camera"}>
          <IconButton className={c.controlButton} onClick={() => { toggleVideo(); setIsVideoOff(!isVideoOff); }}>{isVideoOff ? <VideocamOff /> : <Videocam />}</IconButton>
        </Tooltip>
      </div>
    </>
  );

  return (
    <div className={c.sidebar}>
      <div className={c.desktopOnly}>{settingsBlock}</div>

      <div className={c.mobileSettingsToggleWrap}>
        <Button
          className={c.mobileSettingsToggle}
          variant="contained"
          onClick={() => setShowSettingsMobile((v) => !v)}
          endIcon={showSettingsMobile ? <ExpandLess /> : <ExpandMore />}
        >
          {showSettingsMobile ? "Dölj inställningar" : "Visa inställningar"}
        </Button>
      </div>
      {showSettingsMobile && <div className={c.mobileSettingsPanel}>{settingsBlock}</div>}

      {isTokenMode && callAccepted && !callEnded && (
        <div className={c.section}>
          <Button variant="contained" startIcon={<CallEnd />} fullWidth onClick={leaveCall} className={`${c.button} ${c.hangupButton}`}>Avsluta samtal</Button>
        </div>
      )}

      {isTokenMode && (
        <div className={c.section}>
          <Typography className={c.sectionTitle}>Chatt i rummet</Typography>
          <div className={roomMessages.length > 0 ? c.chatList : c.chatList + " " + c.chatListEmpty}>
                        {roomMessages.map((msg) => (
              <div key={msg.id || `${msg.createdAt}-${msg.senderName}-${msg.message}`} className={c.chatMessage}>
                <strong>{msg.senderName || "Deltagare"}</strong>: {msg.message}
                <div className={c.chatMeta}>{formatTime(msg.createdAt)}</div>
              </div>
            ))}
            <div ref={chatBottomRef} />
          </div>
          <div className={c.chatInputRow}>
            <TextField
              placeholder="Skriv meddelande..."
              value={chatText}
              onChange={(e) => setChatText(e.target.value)}
              onKeyDown={onChatEnter}
              size="small"
              variant="outlined"
              className={c.chatInput}
            />
            <IconButton className={c.sendBtn} onClick={onSendChat}>
              <Send fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}

      {isTokenMode && (
        <div className={c.section}>
          <Typography className={c.sectionTitle}>Rum</Typography>
          <div className={c.roomWrap} onClick={onCopyRoomLink} role="button" tabIndex={0}>
            <img src={qrImageUrl} alt="QR-kod för att ansluta till rummet" className={c.qrImage} />
            <div className={c.roomHint}><LinkIcon fontSize="small" /> Klicka för att kopiera rumslänken</div>
            {roomCopied && <div className={c.roomCopied}>Länk kopierad</div>}
          </div>
        </div>
      )}

      {children}
    </div>
  );
};

export default Sidebar;
